import { Box } from '@mui/material';
import { getEnvironment } from '@shared/utils/common';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { tokenService } from '../../../services/tokenService';
import { resetUserScopedApiState } from '../../../store/api/resetUserScopedApiState';
import type { RootState } from '../../../store/store';
import { useEndImpersonationMutation } from '../api/impersonationApi';

/**
 * Environment color mapping.
 * - Development: Blue
 * - Staging: Purple (#804D7C)
 * - Production: Red (only shown when impersonating)
 */
const ENVIRONMENT_COLORS = {
  development: '#4D6F80',
  staging: '#804D7C',
  production: '#BC4636',
} as const;

function formatTimeRemaining(ms: number): string {
  if (ms <= 0) return 'Expired';
  const minutes = Math.floor(ms / 60000);
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m remaining` : `${hours}h remaining`;
  }
  return `${minutes}m remaining`;
}

function toSafeImpersonationReturnPath(returnPath: string | undefined): string {
  if (!returnPath || returnPath.includes('\\')) {
    return '/admin/coaches';
  }

  try {
    const parsedURL = new URL(returnPath, window.location.origin);
    if (parsedURL.origin !== window.location.origin || !parsedURL.pathname.startsWith('/admin/')) {
      return '/admin/coaches';
    }

    return `${parsedURL.pathname}${parsedURL.search}${parsedURL.hash}`;
  } catch {
    return '/admin/coaches';
  }
}

/**
 * EnvironmentBar displays a colored top bar indicating the current environment.
 *
 * Visibility rules:
 * - Development: Always visible (blue)
 * - Staging: Always visible (purple)
 * - Production: Only visible when impersonating (red)
 *
 * When impersonating, the bar also shows the impersonation target info
 * with an exit button, replacing the separate ImpersonationBanner.
 */
export function EnvironmentBar() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const impersonation = useSelector((state: RootState) => state.auth.impersonation);
  const [endImpersonationApi] = useEndImpersonationMutation();
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);

  const environment = getEnvironment();
  const isImpersonating = impersonation?.active ?? false;

  // In production, only show the bar when impersonating
  const shouldShow = environment !== 'production' || isImpersonating;

  const handleExit = useCallback(async () => {
    const targetPath = toSafeImpersonationReturnPath(impersonation?.returnPath);

    try {
      // Call API to log the end of impersonation
      await endImpersonationApi().unwrap();
    } catch (err) {
      // Log the error but continue - we still want to restore the admin session.
      // The server-side audit log will show the start event, and request-level
      // audit logging captures all data access during the session.
      console.warn('Failed to log impersonation end:', err);
    }

    // Restore original admin session and clear impersonation state atomically.
    tokenService.clearRuntimeImpersonation({ restoreAdminSession: true });

    // CRITICAL: Reset RTK Query caches to clear coach data
    resetUserScopedApiState(dispatch);

    if (typeof window !== 'undefined' && import.meta.env.MODE !== 'test') {
      window.location.assign(targetPath);
      return;
    }

    navigate(targetPath, { replace: true });
  }, [dispatch, endImpersonationApi, impersonation?.returnPath, navigate]);

  // Update time remaining every minute and auto-exit when expired
  useEffect(() => {
    if (!impersonation?.expiresAt) return;

    const updateTimeRemaining = () => {
      const expiresAt = new Date(impersonation.expiresAt).getTime();
      const now = Date.now();
      const remaining = expiresAt - now;
      setTimeRemaining(remaining);

      // Auto-exit when expired
      if (remaining <= 0) {
        handleExit();
      }
    };

    // Update immediately
    updateTimeRemaining();

    // Update every 30 seconds
    const interval = setInterval(updateTimeRemaining, 30000);
    return () => clearInterval(interval);
  }, [impersonation?.expiresAt, handleExit]);

  if (!shouldShow) {
    return null;
  }

  const backgroundColor = ENVIRONMENT_COLORS[environment];
  const environmentLabel = environment.toUpperCase();

  return (
    <Box
      sx={{
        backgroundColor,
        color: 'white',
        py: 0.5,
        px: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '32px',
      }}
    >
      {/* Environment label */}
      <span
        style={{
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          fontSize: '0.75rem',
          fontWeight: 700,
          color: '#FFFFFF',
        }}
      >
        {environmentLabel}
      </span>

      {/* Impersonation info (if active) */}
      {isImpersonating && impersonation && (
        <>
          <span style={{ margin: '0 16px', color: '#FFFFFF' }}>•</span>
          <span style={{ fontWeight: 500, color: '#FFFFFF' }}>
            Viewing as {impersonation.targetName}
          </span>
          <span style={{ opacity: 0.9, marginLeft: '4px', color: '#FFFFFF' }}>
            ({impersonation.targetEmail})
          </span>
          <span style={{ margin: '0 8px', opacity: 0.8, color: '#FFFFFF' }}>• Read-only</span>
          {timeRemaining !== null && (
            <span
              style={{
                fontWeight: timeRemaining < 5 * 60 * 1000 ? 700 : 400,
                color: '#FFFFFF',
              }}
            >
              • {formatTimeRemaining(timeRemaining)}
            </span>
          )}
          <button
            type="button"
            onClick={handleExit}
            style={{
              color: '#FFFFFF',
              marginLeft: '16px',
              textDecoration: 'underline',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.875rem',
            }}
          >
            Exit
          </button>
        </>
      )}
    </Box>
  );
}
